import { EV } from '../core/eventbus.js';
import { Repo } from '../data/schema.js';
import { getItem } from '../data/items.js';
import { formatClock, SEASONS } from '../systems/time.js';
import { personNote } from './insight.js';

const CN = ['零', '一', '二', '三', '四', '五', '六', '七', '八', '九', '十'];
const SHICHEN = ['子', '丑', '寅', '卯', '辰', '巳', '午', '未', '申', '酉', '戌', '亥'];
const WEATHER = { sunny: '晴', cloudy: '阴', rain: '雨', snow: '雪' };

function cnNum(n) {
  if (n <= 10) return CN[n];
  if (n < 20) return '十' + CN[n % 10];
  const t = Math.floor(n / 10), o = n % 10;
  return CN[t] + '十' + (o ? CN[o] : '');
}
function shichen(minute) {
  const h = Math.floor((minute % 1440) / 60);
  return SHICHEN[Math.floor(((h + 1) % 24) / 2)];
}

/**
 * HUD：DOM 覆盖层，只读数据库，不改动世界状态。
 */
export class HUD {
  constructor(engine) {
    this.engine = engine;
    this.db = engine.db;
    this.el = {
      clock: document.getElementById('clock'),
      weather: document.getElementById('weather'),
      toast: document.getElementById('toast'),
      bag: document.getElementById('bag'),
      bagGrid: document.getElementById('bag-grid'),
      help: document.getElementById('help'),
      shelf: document.getElementById('shelf'),
      debug: document.getElementById('debug'),
      stats: document.querySelectorAll('#stats .stat'),
    };
    this._acc = 0;
    this._toasts = [];
    this.bind();
  }

  bind() {
    const { events } = this.engine;

    document.getElementById('btn-bag').onclick = () => this.toggle('bag');
    document.getElementById('btn-help').onclick = () => this.toggle('help');
    document.getElementById('btn-save').onclick = () => this.saveNow();
    document.getElementById('btn-new').onclick = () => {
      if (window.confirm('重置存档？当前进度会清空。')) {
        this.db.reset();
        location.reload();
      }
    };
    for (const el of document.querySelectorAll('[data-close]')) {
      el.onclick = () => this.toggle(el.dataset.close, false);
    }

    events.on(EV.TOAST, ({ text, kind }) => this.toast(text, kind));
    events.on(EV.DIALOG, ({ kind }) => { if (kind === 'bag') this.toggle('bag', true); });
    events.on(EV.INVENTORY_ADD, () => this.refreshBag());

    // Ctrl+S 存档
    window.addEventListener('keydown', (e) => {
      if (e.ctrlKey && (e.key === 's' || e.key === 'S')) {
        e.preventDefault();
        this.saveNow();
      }
      if (e.key === 'Escape') this.closeAll();
    });
  }

  /** 手动存档：顶栏、Ctrl+S、手机右下角「存档」键共用 */
  saveNow() {
    this.db.save(true);
    this.toast('已存档', 'good');
  }

  toggle(name, force) {
    const el = this.el[name];
    if (!el) return;
    const show = force === undefined ? el.classList.contains('hidden') : force;
    el.classList.toggle('hidden', !show);
    if (name === 'bag' && show) this.refreshBag();
  }

  closeAll() { this.toggle('bag', false); this.toggle('help', false); }

  toast(text, kind = 'info') {
    const div = document.createElement('div');
    div.className = 'toast-item ' + kind;
    div.textContent = text;
    this.el.toast.appendChild(div);
    setTimeout(() => {
      div.style.transition = 'opacity .4s';
      div.style.opacity = '0';
      setTimeout(() => div.remove(), 400);
    }, 2200);
    while (this.el.toast.children.length > 4) this.el.toast.firstChild.remove();
  }

  refreshBag() {
    const rows = Repo.items(this.db);
    const grid = this.el.bagGrid;
    this._renderPerson(grid);
    grid.innerHTML = '';
    if (!rows.length) {
      grid.innerHTML = '<div class="slot empty">空空如也</div>';
      return;
    }
    for (const r of rows) {
      const def = getItem(r.itemId);
      const d = document.createElement('div');
      d.className = 'slot';
      d.innerHTML = `<b>${def.name[0]}</b>${def.name}<br>×${r.count}`;
      d.title = def.desc || '';
      grid.appendChild(d);
    }
  }

  /** 行囊顶上那几行：境界 / 身上的照 / 心里攒着的念头（只给名，不给数） */
  _renderPerson(grid) {
    if (!grid || !grid.parentNode) return;
    let host = document.getElementById('bag-person');
    if (!host) {
      host = document.createElement('div');
      host.id = 'bag-person';
      host.style.cssText = 'font-size:13px;color:#6a5c46;line-height:1.9;margin:0 0 10px;'
        + 'padding-bottom:8px;border-bottom:1px solid rgba(0,0,0,.08)';
      grid.parentNode.insertBefore(host, grid);
    }
    const notes = personNote(this.db);
    host.innerHTML = notes.length
      ? notes.map((n) => `<div>${n}</div>`).join('')
      : '<div style="color:#a39a8a">心里还空着，日子过着过着会有的。</div>';
  }

  refreshStats() {
    const p = Repo.getPlayer(this.db);
    const vals = [p.satiety, p.energy, p.mood, Math.min(100, p.qi)];
    this.el.stats.forEach((row, i) => {
      const v = Math.max(0, Math.min(100, vals[i] || 0));
      row.querySelector('i').style.width = v + '%';
      row.querySelector('.num').textContent = Math.round(vals[i] || 0);
    });
  }

  refreshClock() {
    const w = Repo.getWorld(this.db);
    this.el.clock.textContent = `第${cnNum(w.day)}日　${shichen(w.minute)}时　${formatClock(w.minute)}`;
    this.el.weather.innerHTML = `<span class="muted">${SEASONS[w.season] || ''}　</span>${WEATHER[w.weather] || '晴'}`;
  }

  refreshDebug() {
    if (this.el.debug.classList.contains('hidden')) return;
    const e = this.engine.scene && this.engine.scene.first('transform', 'player');
    const t = e ? e.transform : { x: 0, y: 0 };
    const tm = this.engine.scene ? this.engine.scene.tilemap : null;
    const gx = tm ? Math.floor(t.x / tm.tileSize) : 0;
    const gy = tm ? Math.floor(t.y / tm.tileSize) : 0;
    this.el.debug.textContent =
      `FPS ${this.engine.fps.toFixed(0)}\n坐标 ${t.x.toFixed(0)},${t.y.toFixed(0)}\n格 ${gx},${gy}\n朝向 ${e ? e.anim.facing : '-'}`;
  }

  update(dt) {
    this._acc += dt;
    if (this._acc < 0.35) return;
    this._acc = 0;
    this.refreshClock();
    this.refreshStats();
    this.refreshDebug();
    if (!this.el.bag.classList.contains('hidden')) this.refreshBag();
  }
}
